import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import clsx from 'clsx';
import { formatDistance } from 'date-fns';

import { useNavigationStack } from '../../../../context/NavigationStackContext';
import type {
  IPostsViewComment,
  IPostsViewPost,
} from '../../../../redux/api/posts/posts-api.types';
import { AvatarSize } from '../../../../shared/constants';
import { AvatarTypes } from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';
import { orderBy } from '../../../../utils/collections/array-utils';
import { getDateFnsLocale } from '../../../../utils/datetime/date-time-utils';
import { checkIsImage } from '../../../../utils/files/file-utils';
import {
  formatLikeCount,
  getDisplayReactionCount,
} from '../../../../utils/ui/format-like-count';
import AddReactionActionButton from '../../../designe/ActionButtons/AddReactionActionButton';
import ApplyActionButton from '../../../designe/ActionButtons/ApplyActionButton';
import AttachFilesActionButton from '../../../designe/ActionButtons/AttachFilesActionButton';
import CancelActionButton from '../../../designe/ActionButtons/CancleActionButton';
import CommentActionButton from '../../../designe/ActionButtons/CommentActionButton';
import DeleteActionButton from '../../../designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../designe/ActionButtons/EditActionButton';
import RemoveReactionActionButton from '../../../designe/ActionButtons/RemoveReactionActionButton';
import ReplyActionButton from '../../../designe/ActionButtons/ReplyActionButton';
import SendActionButton from '../../../designe/ActionButtons/SendActionButton';
import AppUserAvatar from '../../../designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import AppFile from '../../../designe/app-file/AppFile';
import AppImageGalleryContainer from '../../../designe/app-image-gallery/AppImageGalleryContainer';
import AppButton from '../../../designe/buttons/app-button/AppButton';
import AppTextField from '../../../designe/textfields/app-text-field/AppTextField';
import ConfirmDialog from '../../confirm-dialog/ConfirmDialog';
import Comment from '../comment/Comment';
import { useStyles } from './Post.styles';
import { usePost } from './usePost';

interface IPostProps {
  post: IPostsViewPost;
  editable?: boolean;
  repliedPost?: boolean;
  onPostReplied?: any;
  refreshPosts?: () => void;
}

export default function Post(props: IPostProps) {
  const {
    post,
    editable = true,
    repliedPost = false,
    onPostReplied,
    refreshPosts,
  } = props;
  // const [anchorEl, setAnchorEl] = useState(null);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const [showComments, setShowComments] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { navigate } = useNavigationStack();

  const {
    onPostEdit,
    onToggleToEditPost,
    onCommentEdit,
    onCommentReply,
    onCommentDeleteFromReply,
    onPostSave,
    onPostDelete,
    onCommentSave,
    onPostReaction,
    onUploadFiles,
    onDeleteFile,
    onDeleteCommentFile,
    onUploadCommentFiles,
    onShowMore,
    onPostCancel,
    editedPost,
    newComment,
    commentFiles,
    postFiles,
    userReaction,
    user,
    showedCommentsCount,
    isSavingComment,
    isSavingPost,
    isDeletingPost,
  } = usePost(post, refreshPosts);
  const imagePostFiles =
    postFiles?.filter((file) => checkIsImage(file.mimeType)) ?? [];
  const nonImagePostFiles =
    postFiles?.filter((file) => !checkIsImage(file.mimeType)) ?? [];

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSavingComment) {
        onCommentSave();
      }
    }
  };

  function isEditable() {
    if (!editable) return false;

    return !repliedPost && !editedPost && post.permissions?.canEdit;
  }

  function isDeletable() {
    if (!editable) return false;

    return !repliedPost && !editedPost && post.permissions?.canDelete;
  }

  function renderPostReactionIcon() {
    return userReaction ? (
      <RemoveReactionActionButton onClick={onPostReaction} />
    ) : (
      <AddReactionActionButton onClick={onPostReaction} />
    );
  }

  const isPostLiked =
    userReaction !== null
      ? userReaction
      : !!post.reactions?.find((r) => r.userId === user?.id);

  function onUserClick(userId) {
    navigate(Paths.users.toUser(userId));
  }
  return (
    <div
      className={clsx(
        classes.postWrapper,
        !repliedPost && classes.postWrapperShadow,
      )}>
      <div className={classes.header}>
        <Box onClick={() => onUserClick(post.owner.id)} display="flex">
          <AppUserAvatar
            entity={post.owner}
            size={AvatarSize.m}
            type={AvatarTypes.round}
          />
          <div className={classes.userInfo}>
            <Typography variant="text-lg">
              {`${post.owner.firstname} ${post.owner.lastname || ''}`}
            </Typography>
            <Typography variant="text-sm" className={classes.createdAt}>
              {post?.createdAt &&
                formatDistance(new Date(post.createdAt), new Date(), {
                  addSuffix: true,
                  locale: getDateFnsLocale(i18n.language),
                })}
            </Typography>
          </div>
        </Box>
        <Box display="flex" gap={0.5}>
          {isEditable() && (
            <EditActionButton
              onClick={() => {
                onToggleToEditPost();
              }}
            />
          )}
          {isDeletable() && (
            <DeleteActionButton
              onClick={() => {
                setDeleteConfirm(true);
              }}
            />
          )}

          {/*<AppMenu*/}
          {/*  anchorEl={anchorEl}*/}
          {/*  onClose={() => setAnchorEl(null)}*/}
          {/*  anchorOrigin={getPosition(*/}
          {/*    PositionOption.bottom,*/}
          {/*    PositionOption.right,*/}
          {/*  )}*/}
          {/*  transformOrigin={getPosition(*/}
          {/*    PositionOption.top,*/}
          {/*    PositionOption.right,*/}
          {/*  )}*/}
          {/*  targetElement={*/}
          {/*    <MoreVerticalIcon*/}
          {/*      onClick={(e) => setAnchorEl(e.currentTarget)}*/}
          {/*    />*/}
          {/*  }>*/}
          {/*  {post.ownerId === user.id && (*/}
          {/*    <MenuItem*/}
          {/*      className={classes.menuItem}*/}
          {/*      onClick={() => {*/}
          {/*        onToggleToEditPost();*/}
          {/*        setAnchorEl(null);*/}
          {/*      }}>*/}
          {/*      <EditIcon />*/}
          {/*      {t('posts.editPost')}*/}
          {/*    </MenuItem>*/}
          {/*  )}*/}
          {/*  <MenuItem*/}
          {/*    className={classes.menuItem}*/}
          {/*    onClick={() => {*/}
          {/*      onPostDelete();*/}
          {/*      setAnchorEl(null);*/}
          {/*    }}>*/}
          {/*    <DeleteIcon className={classes.delete} />*/}
          {/*    {t('posts.removePost')}*/}
          {/*  </MenuItem>*/}
          {/*</AppMenu>*/}
        </Box>
      </div>
      <div className={classes.content}>
        {post.parent && !repliedPost && (
          <div className={classes.repliedPostWrapper}>
            <Post post={post.parent} repliedPost />
          </div>
        )}
        {!editedPost && (
          <Box marginTop={1}>
            <Typography className={classes.postContent} variant={'text-md'}>
              {post.content}
            </Typography>
          </Box>
        )}
        {editedPost && (
          <Box marginTop={1} className={classes.editedPostContent}>
            {!!postFiles?.length && (
              <Box marginBottom={1} className={classes.filesWrapper}>
                {postFiles.map((file) => (
                  <AppFile
                    key={file.id}
                    file={file}
                    onDelete={(file) => onDeleteFile(file.id)}
                  />
                ))}
              </Box>
            )}

            <Box display="flex" gap={0.5}>
              <AppTextField
                fullWidth
                multiline
                maxRows={5}
                value={editedPost.content}
                onChange={(e) => onPostEdit(e.target.value)}
              />
              <AttachFilesActionButton
                onFileInput={(e) => onUploadFiles(e.target.files)}
              />
              <ApplyActionButton
                disabled={!editedPost.content && !postFiles.length}
                loading={isSavingPost}
                onClick={onPostSave}
              />
              <CancelActionButton onClick={onPostCancel} />
            </Box>
          </Box>
        )}
        {!editedPost && !!imagePostFiles.length && (
          <Box marginTop={1}>
            <AppImageGalleryContainer files={imagePostFiles} />
          </Box>
        )}
        {!editedPost && !!nonImagePostFiles.length && (
          <div className={classes.filesWrapper}>
            {nonImagePostFiles.map((file) => (
              <AppFile key={file.id} file={file} />
            ))}
          </div>
        )}
      </div>
      {!repliedPost && !editedPost && (
        <>
          <Box
            marginTop={theme.spacing(1)}
            display="flex"
            gap={1}
            alignItems="center">
            <Box display="flex" alignItems="center" gap={0.5}>
              {renderPostReactionIcon()}
              {post.reactions.length > 0 && (
                <Typography
                  component="span"
                  variant="text-sm"
                  sx={{
                    color: isPostLiked
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                  }}>
                  {formatLikeCount(
                    getDisplayReactionCount(
                      post.reactions,
                      user?.id,
                      userReaction,
                    ),
                  )}
                </Typography>
              )}
            </Box>
            <ReplyActionButton onClick={() => onPostReplied(post.id)} />
            {!showComments && (
              <CommentActionButton
                hasComments={!!post.comments?.length}
                onClick={() => setShowComments(true)}
              />
            )}
          </Box>

          {!!post.comments?.length && showComments && (
            <Typography mt={2} variant="h3">
              {t('posts.comments')}({post.comments.length})
            </Typography>
          )}
          {showComments && (
            <div
              className={clsx(
                classes.commentsWrapper,
                (post?.permissions.canComment || !!post.comments.length) &&
                  classes.commentsWrapperBorder,
              )}>
              <div>
                {orderBy<IPostsViewComment>(post.comments, 'createdAt', 'desc')
                  .slice(0, showedCommentsCount)
                  .map((comment: IPostsViewComment) => (
                    <Comment
                      comment={comment}
                      key={comment.id}
                      onCommentReply={onCommentReply}
                      onCommentDeleteFromReply={onCommentDeleteFromReply}
                      allowReply={post?.permissions.canComment}
                      refreshPosts={refreshPosts}
                    />
                  ))}
                {showedCommentsCount < post.comments.length && (
                  <AppButton variant={'text'} onClick={() => onShowMore()}>
                    {t('posts.showMore')}
                  </AppButton>
                )}
              </div>
              {post?.permissions.canComment && (
                <div className={classes.newCommentWrapper}>
                  {!!newComment.parent && (
                    <div className={classes.answeredCommentWrapper}>
                      <Comment comment={newComment.parent} repliedComment />
                    </div>
                  )}

                  {!!commentFiles.length && (
                    <div className={classes.uploadedFilesWrapper}>
                      {commentFiles.map((file) => (
                        <AppFile
                          key={file.id}
                          file={file}
                          onDelete={(file) => onDeleteCommentFile(file.id)}
                        />
                      ))}
                    </div>
                  )}

                  <div className={classes.newCommentData}>
                    <AppTextField
                      placeholder={t('users.newComment')}
                      fullWidth
                      multiline
                      maxRows={5}
                      value={newComment.content}
                      onKeyDown={handleKeyDown}
                      onChange={(e) => onCommentEdit(e.target.value)}
                    />

                    <AttachFilesActionButton
                      onFileInput={(e) => onUploadCommentFiles(e.target.files)}
                    />
                    <SendActionButton
                      onClick={onCommentSave}
                      disabled={!newComment.content && !commentFiles.length}
                      loading={isSavingComment}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {deleteConfirm && (
            <ConfirmDialog
              open={!!deleteConfirm}
              onClose={() => setDeleteConfirm(null)}
              title={t('common.delete')}
              isLoading={isDeletingPost}
              onSubmit={() => {
                void onPostDelete().finally(() => setDeleteConfirm(null));
              }}>
              {t('common.deleteQuestion')}
            </ConfirmDialog>
          )}
        </>
      )}
    </div>
  );
}
